import React, { useEffect, useState } from "react";
import styled, { css } from "styled-components";
import { colors, fonts, media, prefersReducedMotion } from "../../styles/theme";

/* ---------- shared bits ---------- */

const Card = styled.div`
  border-radius: 28px;
  padding: 28px;
  background: ${(p) => (p.$dark ? colors.forest : colors.linen)};
  border: 1px solid ${(p) => (p.$dark ? "rgba(250,247,241,0.12)" : colors.stone)};
  color: ${(p) => (p.$dark ? colors.linen : colors.ink)};

  @media ${media.medium} {
    padding: 40px;
  }
`;

const CardTitle = styled.h3`
  font-family: ${fonts.display};
  font-weight: 300;
  font-size: clamp(28px, 3vw, 40px);
  line-height: 1.05;
  letter-spacing: -0.03em;
  margin-bottom: 24px;
`;

const Label = styled.label`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-family: ${fonts.body};
  font-size: 15px;
  color: ${(p) => (p.$dark ? "rgba(250,247,241,0.75)" : colors.bark)};

  output {
    font-family: ${fonts.mono};
    font-weight: 500;
    color: ${(p) => (p.$dark ? colors.linen : colors.ink)};
  }
`;

const Range = styled.input.attrs({ type: "range" })`
  width: 100%;
  margin: 10px 0 22px;
  accent-color: ${(p) => p.$accent || colors.copper};
  -webkit-appearance: auto;
  appearance: auto;
  cursor: pointer;
`;

const fmtInt = (n) => Math.round(n).toLocaleString("en-US");

const fmtDuration = (minutes) => {
  if (minutes < 60) return `${Math.max(1, Math.round(minutes))} min`;
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes - h * 60);
  return m ? `${h} h ${m} min` : `${h} h`;
};

/* ---------- reading time calculator ---------- */

const Bars = styled.div`
  display: grid;
  gap: 16px;
  margin-top: 8px;
`;

const BarRow = styled.div`
  display: grid;
  gap: 8px;

  .top {
    display: flex;
    justify-content: space-between;
    font-family: ${fonts.body};
    font-size: 15px;
  }
  .top b {
    font-family: ${fonts.display};
    font-weight: 400;
    font-size: 26px;
    letter-spacing: -0.02em;
  }
  .track {
    height: 12px;
    border-radius: 12px;
    background: ${(p) => (p.$dark ? "rgba(250,247,241,0.1)" : colors.sand)};
    overflow: hidden;
  }
  .fill {
    height: 100%;
    border-radius: 12px;
    transition: width 0.6s cubic-bezier(0.2, 0.8, 0.2, 1);
  }
`;

const Saved = styled.p`
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid ${(p) => (p.$dark ? "rgba(250,247,241,0.14)" : colors.stone)};
  font-family: ${fonts.display};
  font-weight: 300;
  font-size: clamp(24px, 2.4vw, 32px);
  letter-spacing: -0.02em;

  em {
    font-style: normal;
    color: ${(p) => (p.$dark ? colors.copperLight : colors.copper)};
  }
`;

const Fine = styled.p`
  margin-top: 12px;
  font-family: ${fonts.body};
  font-size: 13px;
  color: ${(p) => (p.$dark ? "rgba(250,247,241,0.55)" : colors.pebble)};
`;

export const ReadingCalculator = ({ config, accent, dark }) => {
  const [words, setWords] = useState(config.words);
  const [typical, setTypical] = useState(config.typical);
  const [every, setEvery] = useState(20);
  const [breakLen, setBreakLen] = useState(5);
  // The break rule is the reader's own and is applied identically to both bars, so nothing
  // here claims vimbal needs fewer breaks. A shorter read simply crosses fewer intervals.
  const readSlow = words / typical;
  const readFast = words / config.vimbal;
  const breaksIn = (mins) => (every > 0 ? Math.max(0, Math.ceil(mins / every) - 1) : 0);
  const slowBreaks = breaksIn(readSlow);
  const fastBreaks = breaksIn(readFast);
  const slow = readSlow + slowBreaks * breakLen;
  const fast = readFast + fastBreaks * breakLen;
  const max = Math.max(slow, fast);

  return (
    <Card $dark={dark}>
      <CardTitle>{config.title}</CardTitle>
      <Label $dark={dark}>
        {config.wordsLabel}
        <output>{fmtInt(words)}</output>
      </Label>
      <Range
        $accent={accent}
        min={Math.round(config.words / 10)}
        max={config.words * 4}
        step={100}
        value={words}
        onChange={(e) => setWords(Number(e.target.value))}
        aria-label={config.wordsLabel}
      />
      <Label $dark={dark}>
        Your usual reading speed (words per minute)
        <output>{typical}</output>
      </Label>
      <Range
        $accent={accent}
        min={30}
        max={300}
        step={10}
        value={typical}
        onChange={(e) => setTypical(Number(e.target.value))}
        aria-label="Your usual reading speed in words per minute"
      />
      <Label $dark={dark}>
        A break every (minutes)
        <output>{every}</output>
      </Label>
      <Range
        $accent={accent}
        min={5}
        max={60}
        step={5}
        value={every}
        onChange={(e) => setEvery(Number(e.target.value))}
        aria-label="A break every how many minutes"
      />
      <Label $dark={dark}>
        Break length (minutes)
        <output>{breakLen}</output>
      </Label>
      <Range
        $accent={accent}
        min={1}
        max={20}
        step={1}
        value={breakLen}
        onChange={(e) => setBreakLen(Number(e.target.value))}
        aria-label="Break length in minutes"
      />
      <Bars>
        <BarRow $dark={dark}>
          <div className="top">
            <span>Usual pace{slowBreaks > 0 ? ` · ${slowBreaks} break${slowBreaks > 1 ? "s" : ""}` : ""}</span>
            <b>{fmtDuration(slow)}</b>
          </div>
          <div className="track">
            <div className="fill" style={{ width: `${(slow / max) * 100}%`, background: colors.clay }} />
          </div>
        </BarRow>
        <BarRow $dark={dark}>
          <div className="top">
            <span>
              In vimbal at {config.vimbal} wpm
              {fastBreaks > 0 ? ` · ${fastBreaks} break${fastBreaks > 1 ? "s" : ""}` : ""}
            </span>
            <b>{fmtDuration(fast)}</b>
          </div>
          <div className="track">
            <div className="fill" style={{ width: `${(fast / max) * 100}%`, background: accent || colors.moss }} />
          </div>
        </BarRow>
      </Bars>
      <Saved $dark={dark} aria-live="polite">
        {slow > fast ? (
          <>
            That's <em>{fmtDuration(slow - fast)}</em> back.
          </>
        ) : (
          <>You already read faster than this estimate.</>
        )}
      </Saved>
      <Fine $dark={dark}>
        Reading time plus the breaks you set, applied the same way to both. Re-reading and note-taking usually add much
        more to the usual pace.
      </Fine>
    </Card>
  );
};

/* ---------- induction cost calculator ---------- */

const Big = styled.div`
  display: grid;
  gap: 12px;
  margin-top: 8px;

  @media ${media.small} {
    grid-template-columns: 1fr 1fr;
  }

  div {
    padding: 20px;
    border-radius: 18px;
    background: ${colors.limestone};
  }
  span {
    display: block;
    font-family: ${fonts.mono};
    font-size: 11px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: ${colors.bark};
  }
  b {
    display: block;
    margin-top: 8px;
    font-family: ${fonts.display};
    font-weight: 300;
    font-size: clamp(32px, 3.4vw, 44px);
    letter-spacing: -0.03em;
    color: ${colors.ink};
    font-variant-numeric: tabular-nums;
  }
  div:last-child b {
    color: ${colors.terracotta};
  }
`;

export const CostCalculator = ({ accent }) => {
  const [workers, setWorkers] = useState(200);
  const [hours, setHours] = useState(16);
  const [rate, setRate] = useState(60);
  const total = workers * hours * rate;

  return (
    <Card>
      <CardTitle>What could your inductions save?</CardTitle>
      <Label>
        People inducted per year
        <output>{fmtInt(workers)}</output>
      </Label>
      <Range $accent={accent} min={10} max={5000} step={10} value={workers} onChange={(e) => setWorkers(Number(e.target.value))} aria-label="People inducted per year" />
      <Label>
        Induction hours per person
        <output>{hours} h</output>
      </Label>
      <Range $accent={accent} min={2} max={40} step={1} value={hours} onChange={(e) => setHours(Number(e.target.value))} aria-label="Induction hours per person" />
      <Label>
        Hourly cost per person (wages and overheads)
        <output>${rate}</output>
      </Label>
      <Range $accent={accent} min={20} max={200} step={5} value={rate} onChange={(e) => setRate(Number(e.target.value))} aria-label="Hourly cost per person" />
      <Big aria-live="polite">
        <div>
          <span>At measured average (26.5%)</span>
          <b>${fmtInt(total * 0.265)}</b>
        </div>
        <div>
          <span>At measured best (40%)</span>
          <b>${fmtInt(total * 0.4)}</b>
        </div>
      </Big>
      <Fine>Illustrative estimate using the induction time saved when we measured on site. Your results will vary.</Fine>
    </Card>
  );
};

/* ---------- pilot results with tabs ---------- */

const Tabs = styled.div`
  display: inline-flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 5px;
  border-radius: 999px;
  background: ${colors.limestone};
  border: 1px solid ${colors.stone};
  margin-bottom: 28px;
`;

const Tab = styled.button`
  padding: 10px 18px;
  border-radius: 999px;
  background: ${(p) => (p.$on ? colors.ink : "transparent")};
  color: ${(p) => (p.$on ? colors.linen : colors.bark)};
  font-family: ${fonts.body};
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.3s ease, color 0.3s ease;
`;

const Result = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  grid-template-areas: "metric nums" "track track";
  align-items: baseline;
  gap: 10px 16px;
  padding: 18px 0;
  border-top: 1px solid ${colors.stone};

  .metric {
    grid-area: metric;
    font-family: ${fonts.display};
    font-size: 22px;
    letter-spacing: -0.015em;
  }
  .track {
    grid-area: track;
    position: relative;
    height: 14px;
    border-radius: 14px;
    background: ${colors.sand};
    overflow: hidden;
  }
  .best,
  .avg {
    position: absolute;
    inset: 0 auto 0 0;
    border-radius: 14px;
    transition: width 0.8s cubic-bezier(0.2, 0.8, 0.2, 1);
  }
  .best {
    opacity: 0.35;
  }
  .nums {
    grid-area: nums;
    text-align: right;
    font-family: ${fonts.body};
    font-size: 15px;
    color: ${colors.bark};
  }
  .nums b {
    font-family: ${fonts.display};
    font-weight: 400;
    font-size: 28px;
    color: ${colors.ink};
    margin-right: 6px;
  }
`;

const Learning = styled.div`
  margin-top: 20px;
  padding-top: 18px;
  border-top: 1px solid ${colors.sand};

  h4 {
    font-family: ${fonts.mono};
    font-size: 10px;
    font-weight: 500;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: ${colors.moss};
    margin-bottom: 8px;
  }
  p {
    font-family: ${fonts.body};
    font-size: 13px;
    line-height: 1.6;
    color: ${colors.bark};
  }
`;

const GroupLabel = styled.p`
  margin: 4px 0 14px;
  font-family: ${fonts.mono};
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${colors.pebble};
`;

export const PilotResults = ({ data, accent }) => {
  const [tab, setTab] = useState(data.groups[0].key);
  const group = data.groups.find((g) => g.key === tab);
  return (
    <Card>
      <CardTitle>{data.label}</CardTitle>
      {data.groups.length > 1 && (
      <Tabs role="group" aria-label="Pilot group">
        {data.groups.map((g) => (
          <Tab key={g.key} type="button" $on={g.key === tab} aria-pressed={g.key === tab} onClick={() => setTab(g.key)}>
            {g.tab}
          </Tab>
        ))}
      </Tabs>
      )}
      {data.groups.length === 1 && <GroupLabel>{group.tab}</GroupLabel>}
      <div aria-live="polite">
        {group.rows.map((r) => (
          <Result key={r.metric}>
            <span className="metric">{r.metric}</span>
            <div className="track" aria-hidden="true">
              <span className="best" style={{ width: `${Math.min(100, r.best / 1.6)}%`, background: accent }} />
              <span className="avg" style={{ width: `${Math.min(100, r.avg / 1.6)}%`, background: accent }} />
            </div>
            <span className="nums">
              <b>
                {r.dir === "up" ? "↑" : "↓"} {r.avg}%
              </b>
              avg · up to {r.best}%
            </span>
          </Result>
        ))}
      </div>
      <Fine>{data.note}</Fine>
      {data.learning && (
        <Learning>
          <h4>{data.learning.label}</h4>
          <p>{data.learning.body}</p>
        </Learning>
      )}
    </Card>
  );
};

/* ---------- brain chemistry explorer ---------- */

const ChemGrid = styled.div`
  display: grid;
  gap: 20px;

  @media ${media.medium} {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1.1fr);
    align-items: stretch;
  }
`;

const ChemList = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
`;

const ChemBtn = styled.button`
  text-align: left;
  padding: 16px 18px;
  border-radius: 18px;
  cursor: pointer;
  border: 1px solid ${(p) => (p.$on ? colors.ink : colors.stone)};
  background: ${(p) => (p.$on ? colors.ink : colors.linen)};
  color: ${(p) => (p.$on ? colors.linen : colors.ink)};
  transition: all 0.3s ease;

  b {
    display: block;
    font-family: ${fonts.display};
    font-weight: 400;
    font-size: 20px;
    letter-spacing: -0.015em;
  }
  span {
    font-family: ${fonts.mono};
    font-size: 11px;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    opacity: 0.7;
  }
  &:hover {
    border-color: ${colors.ink};
  }
`;

const ChemDetail = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 28px;
  padding: 36px;
  background: ${colors.forest};
  color: ${colors.linen};
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  min-height: 280px;

  .formula {
    position: absolute;
    top: 24px;
    right: 28px;
    font-family: ${fonts.mono};
    font-size: 12px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: ${colors.sage};
  }
  h4 {
    font-family: ${fonts.display};
    font-weight: 300;
    font-size: clamp(36px, 4vw, 56px);
    letter-spacing: -0.03em;
    line-height: 1;
  }
  h4 em {
    display: block;
    font-size: 0.5em;
    color: ${colors.copperLight};
    margin-top: 8px;
  }
  p {
    margin-top: 18px;
    font-family: ${fonts.body};
    font-size: 18px;
    line-height: 1.55;
    color: rgba(250, 247, 241, 0.82);
  }
`;

export const ChemistryExplorer = ({ items }) => {
  const [idx, setIdx] = useState(0);
  const item = items[idx];
  return (
    <ChemGrid>
      <ChemList role="group" aria-label="Brain chemicals">
        {items.map((c, i) => (
          <ChemBtn key={c.name} type="button" $on={i === idx} aria-pressed={i === idx} onClick={() => setIdx(i)} onMouseEnter={() => setIdx(i)}>
            <b>{c.name}</b>
            <span>{c.role}</span>
          </ChemBtn>
        ))}
      </ChemList>
      <ChemDetail aria-live="polite">
        <span className="formula">
          {idx + 1} / {items.length}
        </span>
        <h4>
          {item.name}
          <em>{item.role}</em>
        </h4>
        <p>{item.body}</p>
      </ChemDetail>
    </ChemGrid>
  );
};

/* ---------- breathing orb ---------- */

const PHASES = [
  { label: "Breathe in", secs: 4, scale: 1 },
  { label: "Hold", secs: 2, scale: 1 },
  { label: "Breathe out", secs: 6, scale: 0.55 },
];

const OrbWrap = styled.div`
  position: relative;
  display: grid;
  place-items: center;
  min-height: 420px;
  border-radius: 32px;
  overflow: hidden;
  background:
    radial-gradient(60% 60% at 50% 50%, rgba(157, 184, 191, 0.25), transparent 70%),
    ${colors.forest};
`;

const Orb = styled.div`
  width: 260px;
  height: 260px;
  border-radius: 50%;
  background: radial-gradient(circle at 35% 30%, ${colors.mist}, ${colors.moss} 60%, ${colors.ocean});
  box-shadow: 0 0 80px rgba(163, 177, 143, 0.45);
  transform: scale(${(p) => p.$scale});
  transition: transform ${(p) => p.$secs}s ease-in-out;

  ${(p) =>
    p.$still &&
    css`
      transition: none;
    `}
`;

const OrbText = styled.div`
  position: absolute;
  text-align: center;
  color: ${colors.linen};
  pointer-events: none;

  b {
    display: block;
    font-family: ${fonts.display};
    font-weight: 300;
    font-size: 34px;
    letter-spacing: -0.02em;
  }
  span {
    font-family: ${fonts.mono};
    font-size: 12px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    opacity: 0.75;
  }
`;

const OrbButton = styled.button`
  position: absolute;
  bottom: 24px;
  padding: 12px 22px;
  border-radius: 999px;
  border: 1px solid rgba(250, 247, 241, 0.35);
  background: rgba(250, 247, 241, 0.08);
  color: ${colors.linen};
  font-family: ${fonts.body};
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: rgba(250, 247, 241, 0.16);
  }
`;

export const BreathingOrb = () => {
  const [running, setRunning] = useState(false);
  const [phase, setPhase] = useState(2);
  const reduced = prefersReducedMotion();

  useEffect(() => {
    if (!running) return undefined;
    const id = setTimeout(() => setPhase((p) => (p + 1) % PHASES.length), PHASES[phase].secs * 1000);
    return () => clearTimeout(id);
  }, [running, phase]);

  const current = PHASES[phase];
  const toggle = () => {
    if (running) {
      setRunning(false);
      setPhase(2);
    } else {
      setPhase(0);
      setRunning(true);
    }
  };

  return (
    <OrbWrap>
      <Orb $scale={running ? current.scale : 0.7} $secs={current.secs} $still={reduced} aria-hidden="true" />
      <OrbText aria-live="polite">
        <b>{running ? current.label : "Take a minute"}</b>
        <span>{running ? `${current.secs} seconds` : "A small taste of calm"}</span>
      </OrbText>
      <OrbButton type="button" onClick={toggle}>
        {running ? "Stop" : "Start breathing"}
      </OrbButton>
    </OrbWrap>
  );
};
